import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, Popover, Text } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDualSlider } from 'components/common/form/dual-slider';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { MachineContext } from 'contexts/machine.context';
import useOnClickAway from 'hooks/useOnClickAway';
import { t } from 'i18next';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { INumericRangeFilter } from 'lib_ts/interfaces/common/i-num-range-filter';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { IPitchMetricsFilters } from 'lib_ts/interfaces/pitches/i-pitch-metrics-filters';
import { isNil } from 'lodash';
import { useCallback, useContext, useRef, useState } from 'react';

const getMinMaxFilter = ({
  key,
  value,
  acc,
}: {
  key: keyof IPitchMetricsFilters;
  value?: number | null;
  acc: IPitchMetricsFilters;
}): [number, number] | null => {
  const { getMin, getMax } = MiscHelper;

  if (isNil(value)) {
    return null;
  }

  if (acc[key].minMax) {
    const existing = acc[key].minMax as [number, number];
    return [getMin([value, existing[0]]), getMax([value, existing[1]])];
  }

  return [value, value];
};

const generateFilters = (pitches: IPitch[], isMetric?: boolean) =>
  pitches.reduce(
    (acc, { breaks, bs }) => {
      const { vnet, wnet } = bs;

      const vnetFilter = getMinMaxFilter({
        key: 'vnet',
        value: vnet,
        acc,
      });
      acc.vnet.minMax = vnetFilter;
      acc.vnet.value = vnetFilter;

      const wnetFilter = getMinMaxFilter({
        key: 'wnet',
        value: wnet,
        acc,
      });
      acc.wnet.minMax = wnetFilter;
      acc.wnet.value = wnetFilter;

      if (breaks) {
        const { xInches, zInches } = breaks;

        const xBreakINfilter = getMinMaxFilter({
          key: 'xInches',
          value: xInches ? -1 * xInches : undefined,
          acc,
        });
        acc.xInches.minMax = xBreakINfilter;
        acc.xInches.value = xBreakINfilter;

        const zBreakINfilter = getMinMaxFilter({
          key: 'zInches',
          value: zInches,
          acc,
        });
        acc.zInches.minMax = zBreakINfilter;
        acc.zInches.value = zBreakINfilter;
      }

      return acc;
    },
    {
      vnet: {
        label: isMetric
          ? t('common.pitch-speed-kph')
          : t('common.pitch-speed-mph'),
        minMax: null,
        value: null,
        step: 0.1,
        roundTo: 1,
        transform: isMetric ? MetricHelper.mphToKph : undefined,
      },
      wnet: {
        label: t('common.net-spin'),
        minMax: null,
        value: null,
        step: 0.1,
        roundTo: 1,
      },
      xInches: {
        label: isMetric
          ? t('common.horizontal-break-cm')
          : t('common.horizontal-break-in'),
        minMax: null,
        value: null,
        step: 0.1,
        roundTo: 1,
        transform: isMetric ? MetricHelper.inToCm : undefined,
      },
      zInches: {
        label: isMetric
          ? t('common.vertical-break-cm')
          : t('common.vertical-break-in'),
        minMax: null,
        value: null,
        step: 0.1,
        roundTo: 1,
        transform: isMetric ? MetricHelper.inToCm : undefined,
      },
    } as IPitchMetricsFilters
  );

export const PLPitchMetricsControls = () => {
  const [open, setOpen] = useState(false);

  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  const onClickAway = useCallback(() => {
    setOpen(false);
  }, []);

  useOnClickAway({
    ref: popoverRef,
    shouldListen: open,
    onClickAway,
    // don't handle clicks on the trigger element to prevent re-opening popover
    ignoreEls: [triggerRef.current],
  });

  const loading = usePitchListStore((s) => s.loading);

  return (
    <ErrorBoundary identifier="PLPitchMetricsControls">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger>
          <button
            className="PitchMetricsButton"
            disabled={loading}
            ref={triggerRef}
            type="button"
          >
            <Flex gap="1" align="center" justify="between" width="100%">
              <Text size="2">{t('common.pitch-metrics')}</Text>
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Flex>
          </button>
        </Popover.Trigger>
        <Popover.Content align="end" ref={popoverRef}>
          <Content onClose={() => setOpen(false)} />
        </Popover.Content>
      </Popover.Root>
    </ErrorBoundary>
  );
};

const Content = (props: { onClose: () => void }) => {
  const { machine } = useContext(MachineContext);
  const pitches = usePitchListStore((s) => s.pitches);
  const metricsFilters = usePitchListStore((s) => s.metricsFilters);

  // store these two values in refs to avoid unnecessary re-calculations as the slider values change
  const defaultInputsRef = useRef(
    metricsFilters || generateFilters(pitches, machine?.isMetric)
  );

  const hasVisibleFiltersRef = useRef(
    (() => {
      const visibleFilters = Object.keys(defaultInputsRef.current).reduce(
        (acc, key) => {
          const filterKey = key as keyof IPitchMetricsFilters;
          const filter = defaultInputsRef.current[filterKey];

          const { minMax } = filter;

          // has distinct min and max values
          if (minMax && minMax[0] !== minMax[1]) {
            acc.push(filter);
          }

          return acc;
        },
        [] as INumericRangeFilter[]
      );

      return visibleFilters.length > 0;
    })()
  );

  // Controlled inputs scoped to this component only to minimize unnecessary re-renders
  const [inputs, setInputs] = useState<IPitchMetricsFilters>(
    defaultInputsRef.current
  );

  return (
    <Flex
      p="4"
      direction="column"
      gap={RADIX.FLEX.GAP.MD}
      width={hasVisibleFiltersRef.current ? '375px' : 'auto'}
    >
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        {!hasVisibleFiltersRef.current && (
          <Box>
            <Text>{t('common.no-advanced-filters')}</Text>
          </Box>
        )}

        {Object.keys(inputs).map((key) => {
          const filter = inputs[key as keyof IPitchMetricsFilters];

          const { label, minMax, value, step, roundTo, transform } = filter;

          // Ignore if column has no data or only one value
          if (!minMax || minMax[0] === minMax[1]) {
            return null;
          }

          return (
            <CommonDualSlider
              id={key}
              key={key}
              label={label}
              defaultValue={minMax}
              min={minMax[0]}
              max={minMax[1]}
              step={step}
              minStepsBetweenThumbs={step}
              roundTo={roundTo}
              value={value as [number, number]}
              transform={transform}
              onValueChange={(value) =>
                setInputs((prev) => ({
                  ...prev,
                  [key]: {
                    ...prev[key as keyof IPitchMetricsFilters],
                    value,
                  },
                }))
              }
            />
          );
        })}
      </Flex>

      {hasVisibleFiltersRef.current && (
        <Flex justify="end" gap={RADIX.FLEX.GAP.MD}>
          <Button
            variant="soft"
            size={RADIX.BUTTON.SIZE.SM}
            color={RADIX.COLOR.NEUTRAL}
            onClick={() => {
              setInputs(defaultInputsRef.current);
              usePitchListStore.getState().setMetricsFilters(null);
              props.onClose();
            }}
          >
            {t('common.reset')}
          </Button>
          <Button
            variant="solid"
            size={RADIX.BUTTON.SIZE.SM}
            onClick={() => {
              usePitchListStore.getState().setMetricsFilters(inputs);
              props.onClose();
            }}
          >
            {t('common.apply-filters')}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
